import type { ReactNode } from "react";

/**
 * Minimal inline markdown renderer for article bodies.
 *
 * Supports `[label](url)` links only — everything else stays literal text.
 * Editorial articles need real, crawlable outbound links (for example to the
 * JYNTIX product pages) and internal links, so paragraphs and bullets are
 * rendered through this helper instead of as plain text.
 */
const LINK = /\[([^\]]+)\]\((https?:\/\/[^\s)]+|\/[^\s)]*)\)/g;

export function ArticleRichText({ text }: { text: string }): ReactNode {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  LINK.lastIndex = 0;
  while ((match = LINK.exec(text)) !== null) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));
    const label = match[1]!;
    const href = match[2]!;
    const external = href.startsWith("http");
    nodes.push(
      <a
        key={`${href}-${match.index}`}
        href={href}
        {...(external ? { target: "_blank", rel: "noopener" } : {})}
        className="font-semibold text-primary underline underline-offset-4 hover:no-underline"
      >
        {label}
      </a>,
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes.length > 0 ? nodes : text;
}
